import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useLanguage } from "@/context/LanguageContext";

export function PersonalStep({ value, onChange, errors }) {
  const { t } = useLanguage();
  const p = t.personal;
  const set = (k, v) => onChange({ ...value, [k]: v });

  return (
    <div className="space-y-6" data-testid="step-personal-content">
      <div>
        <h2 className="text-3xl font-black tracking-tight md:text-4xl">
          {p.title}
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">{p.description}</p>
      </div>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <Field id="family_name" label={p.family_name} required error={errors?.family_name}>
          <Input
            id="family_name"
            data-testid="personal-family-name"
            value={value.family_name || ""}
            onChange={(e) => set("family_name", e.target.value)}
            className="h-12"
          />
        </Field>
        <Field id="given_name" label={p.given_name} required error={errors?.given_name}>
          <Input
            id="given_name"
            data-testid="personal-given-name"
            value={value.given_name || ""}
            onChange={(e) => set("given_name", e.target.value)}
            className="h-12"
          />
        </Field>
        <Field id="middle_name" label={p.middle_name}>
          <Input
            id="middle_name"
            data-testid="personal-middle-name"
            value={value.middle_name || ""}
            onChange={(e) => set("middle_name", e.target.value)}
            className="h-12"
          />
        </Field>
        <Field id="date_of_birth" label={p.date_of_birth} required error={errors?.date_of_birth}>
          <Input
            id="date_of_birth"
            data-testid="personal-dob"
            type="date"
            value={value.date_of_birth || ""}
            onChange={(e) => set("date_of_birth", e.target.value)}
            className="h-12"
          />
        </Field>
        <Field id="gender" label={p.gender}>
          <Select
            value={value.gender || ""}
            onValueChange={(v) => set("gender", v)}
          >
            <SelectTrigger
              id="gender"
              data-testid="personal-gender"
              className="h-12"
            >
              <SelectValue placeholder="—" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="MALE">{p.gender_options.MALE}</SelectItem>
              <SelectItem value="FEMALE">{p.gender_options.FEMALE}</SelectItem>
              <SelectItem value="OTHER">{p.gender_options.OTHER}</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field id="ssn" label={p.ssn} hint={value._ssn_placeholder || p.ssn_help}>
          <Input
            id="ssn"
            data-testid="personal-ssn"
            value={value.social_security_number || ""}
            onChange={(e) => set("social_security_number", e.target.value)}
            placeholder={value._ssn_placeholder ? "XXX-XX-XXXX" : "XXX-XX-XXXX"}
            className="h-12 font-mono"
          />
        </Field>
        <Field id="a_number" label={p.a_number} hint={p.a_number_help}>
          <Input
            id="a_number"
            data-testid="personal-anumber"
            value={value.alien_registration_number || ""}
            onChange={(e) => set("alien_registration_number", e.target.value)}
            placeholder="123456789"
            className="h-12 font-mono"
          />
        </Field>
        <Field id="email" label={p.email}>
          <Input
            id="email"
            data-testid="personal-email"
            type="email"
            value={value.email || ""}
            onChange={(e) => set("email", e.target.value)}
            className="h-12"
          />
        </Field>
        <Field id="phone" label={p.phone}>
          <Input
            id="phone"
            data-testid="personal-phone"
            value={value.phone || ""}
            onChange={(e) => set("phone", e.target.value)}
            className="h-12"
          />
        </Field>
      </div>
    </div>
  );
}

function Field({ id, label, required, hint, error, children }) {
  return (
    <div className="flex flex-col gap-2">
      <Label
        htmlFor={id}
        className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground"
      >
        {label}
        {required && <span className="ml-1 text-[hsl(var(--warning))]">*</span>}
      </Label>
      {children}
      {hint && !error && (
        <p className="text-[11px] text-muted-foreground">{hint}</p>
      )}
      {error && (
        <p className="text-[11px] text-destructive" aria-live="polite">
          {error}
        </p>
      )}
    </div>
  );
}
